// src/stores/crmActivityStore.js
import { defineStore } from "pinia";
import service from "@/services/crm/crm-activity.service";
import * as Toast from "@/utils/toast";

// ✅ Helper to handle different toast API formats
const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};

const notifySuccess = (msg) => {
  if (Toast?.toast?.success) return Toast.toast.success(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "success");
  if (Toast?.success) return Toast.success(msg);
  if (typeof Toast === "function") return Toast(msg, "success");
  console.log(msg);
};

export const useCrmActivityStore = defineStore("crmActivity", {
  state: () => ({
    loading: false,
    items: [],
    byId: Object.create(null),
    meta: { total: 0, per_page: 25, current_page: 1 },
    params: {
      q: "",
      type: "",
      status: "",
      owner_id: "",
      date_from: "",
      date_to: "",
      sort: "created_desc",
      page: 1,
      per_page: 25,
    },
    selectedIds: [],
  }),
  actions: {
    normalize(raw = {}) {
      return {
        id: raw.id,
        title: raw.title || "",
        type: raw.type || "call",
        due_at: raw.due_at || null,
        reminder_minutes: raw.reminder_minutes || null,
        owner_id: raw.owner_id || null,
        owner: raw.owner || null,
        status: raw.status || "open",
        description: raw.description || "",
        link_type: raw.link_type || null,
        link_id: raw.link_id || null,
        link: raw.link || null,
        created_at: raw.created_at || null,
        updated_at: raw.updated_at || null,
      };
    },

    upsertOne(raw) {
      const n = this.normalize(raw);
      this.byId[n.id] = n;
      const idx = this.items.findIndex((x) => x.id === n.id);
      if (idx === -1) this.items.unshift(n);
      else this.items.splice(idx, 1, n);
      return n;
    },

    async load(overrides = {}) {
      this.loading = true;
      try {
        const params = { ...this.params, ...overrides };
        const { data } = await service.list(params);
        
        const rawItems = data.data || data.items || [];
        this.items = rawItems.map(raw => this.upsertOne(raw));
        
        this.meta = {
          total: data.total || data.meta?.total || 0,
          per_page: data.per_page || data.meta?.per_page || 25,
          current_page: data.current_page || data.meta?.current_page || 1,
        };
        this.params.page = this.meta.current_page;
        this.params.per_page = this.meta.per_page;
      } catch (e) {
        notifyError(e?.response?.data?.message || e.message || "Failed to load activities");
      } finally {
        this.loading = false;
      }
    },

    async get(id) {
      if (!id) return null;
      this.loading = true;
      try {
        const { data } = await service.get(id);
        const normalized = this.upsertOne(data);
        return { data: normalized };
      } catch (e) {
        notifyError(e?.response?.data?.message || e.message || "Failed to load activity");
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async show(id) {
      return await this.get(id);
    },

    async find(id) {
      return await this.get(id);
    },

    async create(payload) {
      this.loading = true;
      try {
        const { data } = await service.create(payload);
        const normalized = this.upsertOne(data);
        notifySuccess("Activity created");
        return normalized;
      } catch (e) {
        notifyError(e?.response?.data?.message || e.message || "Create failed");
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async update(id, payload) {
      this.loading = true;
      try {
        const { data } = await service.update(id, payload);
        const normalized = this.upsertOne(data);
        notifySuccess("Activity updated");
        return normalized;
      } catch (e) {
        notifyError(e?.response?.data?.message || e.message || "Update failed");
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async remove(id) {
      this.loading = true;
      try {
        await service.delete(id);
        
        // Remove from local state
        delete this.byId[id];
        const idx = this.items.findIndex(x => x.id === id);
        if (idx !== -1) this.items.splice(idx, 1);
        
        notifySuccess("Activity deleted");
      } catch (e) {
        notifyError(e?.response?.data?.message || e.message || "Delete failed");
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async bulkDelete() {
      if (!this.selectedIds.length) return;
      this.loading = true;
      try {
        await service.bulkDelete(this.selectedIds);
        
        // Remove from local state
        this.selectedIds.forEach(id => {
          delete this.byId[id];
          const idx = this.items.findIndex(x => x.id === id);
          if (idx !== -1) this.items.splice(idx, 1);
        });
        
        this.selectedIds = [];
        notifySuccess("Activities deleted");
      } catch (e) {
        notifyError(e?.response?.data?.message || e.message || "Delete failed");
        throw e;
      } finally {
        this.loading = false;
      }
    },

    // Helper to toggle selection
    toggleSelect(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx === -1) {
        this.selectedIds.push(id);
      } else {
        this.selectedIds.splice(idx, 1);
      }
    },

    // Select all items
    selectAll() {
      this.selectedIds = this.items.map(x => x.id);
    },

    // Clear selection
    clearSelection() {
      this.selectedIds = [];
    },
  },
});